// src/ui/DocBubble.jsx — the document card.
//
// Two layouts from one component, matching the phone: with a first-page preview it's a
// tall card (preview strip, divider, then the meta row); without one it's just the meta
// row. `hasPreview` is simply whether the thumbnail decrypted.
import React from 'react';
import { Doc, Download, Lock } from './icons';
import { docStyle, humanBytes } from '../services/documents';

export default function DocBubble({ m, hostRef }) {
  const name = m.documentName || 'Document';
  const { ext, color } = docStyle(name);
  const size = humanBytes(m.documentSize);
  const thumb = m._docThumb;
  const ready = !!m._media;

  // 🟥 RESERVE THE PREVIEW'S HEIGHT BEFORE IT DECRYPTS. The strip used to render only
  //    once `_docThumb` resolved, so every document bubble opened short and then grew by
  //    a few hundred pixels the moment its first page came back. In a chat that's mostly
  //    documents that's a dozen bubbles all growing at once, a few hundred milliseconds
  //    apart — the transcript reflows under the viewport, and the open-at-the-bottom
  //    scroll lands somewhere that stops being the bottom a beat later. Knowing the
  //    thumbnail EXISTS is enough to hold its space; the picture just fades into a box
  //    that was already the right size.
  //    (`docThumbUrl` is the same thing arriving through the phone's history export.)
  // 🟥 AND THE RESERVATION MUST BE ABLE TO GIVE UP. Reserving on "a thumb URL exists"
  //    alone meant a thumbnail whose decrypt FAILED — no copy sealed for this browser,
  //    a dead URL — held its blank strip forever: a white rectangle where a first page
  //    should be. So the reservation gives up once the preview has been TRIED, and the
  //    card collapses to the plain icon row — the honest version of itself.
  //
  // 🟥 AND IT MUST BE _docThumbTried, NOT _mediaResolved. That was the vanishing-preview
  //    bug. `_mediaResolved` means "the media queue is finished with this message", and
  //    it is set by paths that never looked at the thumbnail at all — including the
  //    early bail-out for a message with no cloud file. So the strip was told to give up
  //    on a preview nobody had attempted yet, which is precisely "it flashed and
  //    disappeared". `_docThumbTried` is set only where the thumbnail decrypt actually
  //    ran, so the strip waits for its own answer rather than someone else's.
  const hasPreview = !!thumb || ((!!m.documentThumbUrl || !!m.docThumbUrl) && !m._docThumbTried);

  // the phone sizes the preview strip by the thumbnail's own aspect, defaulting to 0.75
  const ratio = (m.documentThumbWidth && m.documentThumbHeight)
    ? m.documentThumbWidth / m.documentThumbHeight
    : 0.75;

  const open = (e) => {
    if (!ready) { e.preventDefault(); }
  };

  return (
    <a
      ref={hostRef}
      className={`doc ${hasPreview ? 'has-preview' : ''}`}
      href={m._media || '#'}
      download={name}
      target="_blank"
      rel="noreferrer"
      onClick={open}
      title={ready ? `Download ${name}` : 'Decrypting…'}
    >
      {hasPreview && (
        <span className={`doc-preview ${thumb ? '' : 'pending'}`} style={{ aspectRatio: String(ratio) }}>
          {thumb && <img src={thumb} alt="" />}
          <span className="doc-preview-fade" />
        </span>
      )}

      <span className="doc-row">
        <span className="doc-tile" style={{ background: color }}>
          <Doc width={18} height={18} />
          <span className="doc-ext">{ext || 'FILE'}</span>
        </span>

        <span className="doc-meta">
          <span className="doc-name">{name}</span>
          <span className="doc-sub">
            {ready
              ? [ext, size].filter(Boolean).join(' · ')
              : <><Lock /> Decrypting…</>}
          </span>
        </span>

        <span className="doc-dl">
          {m._uploadPct != null && m._uploadPct < 100
            ? <span className="doc-pct">{Math.round(m._uploadPct)}%</span>
            : ready ? <Download width={17} height={17} /> : <span className="mb-spin sm" />}
        </span>
      </span>
    </a>
  );
}
